/* eslint-disable react/forbid-prop-types, react/require-default-props */
import React, { useState } from 'react';
import PropTypes from 'prop-types';
import styled from 'styled-components';

import Modal from '../../../../components/Modal/Modal';
import { SmallButton } from '../../../../styles/ui/Button';
import RestoreItemDialog from '../../RoutingItemShow/components/RestoreItemDialog';

// =============================================================================
// CancelledItemsModal — lista dos itens EXCLUÍDOS (soft delete → CANCELADO)
// daquela roteirização. Alimentado pelo balde `cancelled[]` do snapshot
// (handoff 0048).
//
// Handoff 0059: ganhou a ação "Restaurar" por item (canRestore — exclusiva de
// routing:schedule_item:cancel; sem a permissão a lista segue view-only). Após
// o 200 o pai recarrega o snapshot: o item migra pra NÃO ROTEIRIZADOS e some
// desta lista.
// =============================================================================

const Intro = styled.p`
   margin: 0 0 12px;
   font-size: 0.8rem;
   line-height: 1.5;
   color: ${({ theme }) => theme?.colors?.textSecondary ?? '#555'};
`;

const List = styled.ul`
   list-style: none;
   margin: 0;
   padding: 0;
   display: flex;
   flex-direction: column;
   gap: 8px;
   max-height: 60vh;
   overflow-y: auto;
`;

const Item = styled.li`
   border: 1px solid rgba(0, 0, 0, 0.1);
   border-radius: 10px;
   padding: 10px 12px;
   display: flex;
   flex-direction: column;
   gap: 2px;
`;

const TopRow = styled.div`
   display: flex;
   align-items: baseline;
   justify-content: space-between;
   gap: 10px;
`;

const OrderNo = styled.span`
   font-weight: 700;
   font-size: 0.9rem;
`;

const Value = styled.span`
   font-size: 0.8rem;
   color: ${({ theme }) => theme?.colors?.textSecondary ?? '#555'};
   white-space: nowrap;
`;

const Customer = styled.div`
   font-size: 0.85rem;
`;

const Addr = styled.div`
   font-size: 0.78rem;
   color: ${({ theme }) => theme?.colors?.textSecondary ?? '#666'};
`;

const Meta = styled.div`
   margin-top: 4px;
   font-size: 0.72rem;
   color: #8b8b82;
`;

const Reason = styled.div`
   margin-top: 4px;
   font-size: 0.76rem;
   color: ${({ theme }) => theme?.colors?.textSecondary ?? '#555'};

   strong {
      font-weight: 600;
   }
`;

const ActionRow = styled.div`
   margin-top: 8px;
   display: flex;
   justify-content: flex-end;
`;

function formatBRL(value) {
   if (value == null) return null;
   return Number(value).toLocaleString('pt-BR', {
      style: 'currency',
      currency: 'BRL',
   });
}

// ISO → "DD/MM/YYYY HH:MM" (local). Vazio/inválido → null.
function formatDateTime(iso) {
   if (!iso) return null;
   const d = new Date(iso);
   if (Number.isNaN(d.getTime())) return null;
   const pad = (n) => String(n).padStart(2, '0');
   return `${pad(d.getDate())}/${pad(d.getMonth() + 1)}/${d.getFullYear()} ${pad(
      d.getHours()
   )}:${pad(d.getMinutes())}`;
}

export default function CancelledItemsModal({
   items = [],
   canRestore = false,
   onRestored = null,
   onClose,
}) {
   // Item alvo do RestoreItemDialog — { id, order_number } | null.
   const [restoreTarget, setRestoreTarget] = useState(null);

   return (
      <Modal
         isOpen
         onClose={onClose}
         title={`Itens excluídos (${items.length})`}
         maxWidth="560px"
      >
         <Intro>
            Itens removidos desta roteirização (exclusão é um cancelamento — soft
            delete). Eles não entram nas rotas, na otimização nem nos relatórios.
            {canRestore
               ? ' Restaurar devolve o item para "Não roteirizados", como Pendente.'
               : ' A restauração é feita pela equipe de roteirização.'}
         </Intro>

         {items.length === 0 ? (
            <Addr>Nenhum item excluído nesta roteirização.</Addr>
         ) : (
            <List>
               {items.map((it) => {
                  const when = formatDateTime(it.cancelledAt);
                  const who = it.cancelledBy?.nome ?? null;
                  const value = formatBRL(it.raw?.order_value);
                  // Motivo da exclusão (handoff 0048). Itens excluídos antes
                  // desta entrega vêm sem motivo (null) → rótulo neutro.
                  const reason = it.cancellationReason?.description ?? null;
                  return (
                     <Item key={it.itemId}>
                        <TopRow>
                           <OrderNo>{it.id}</OrderNo>
                           {value && <Value>{value}</Value>}
                        </TopRow>
                        <Customer>{it.nm}</Customer>
                        <Addr>{it.addr}</Addr>
                        <Reason>
                           <strong>Motivo:</strong>{' '}
                           {reason || 'Sem motivo registrado'}
                        </Reason>
                        {(when || who) && (
                           <Meta>
                              Excluído
                              {when ? ` em ${when}` : ''}
                              {who ? ` por ${who}` : ''}
                           </Meta>
                        )}
                        {canRestore && (
                           <ActionRow>
                              <SmallButton
                                 type="button"
                                 $intent="write"
                                 onClick={() =>
                                    setRestoreTarget({
                                       id: it.itemId,
                                       order_number:
                                          it.raw?.order_number ?? null,
                                    })
                                 }
                              >
                                 Restaurar
                              </SmallButton>
                           </ActionRow>
                        )}
                     </Item>
                  );
               })}
            </List>
         )}

         <RestoreItemDialog
            isOpen={Boolean(restoreTarget)}
            onClose={() => setRestoreTarget(null)}
            item={restoreTarget}
            onRestored={() => {
               setRestoreTarget(null);
               onRestored?.();
            }}
         />
      </Modal>
   );
}

CancelledItemsModal.propTypes = {
   // Stops adaptados do balde cancelled[] (adaptItemToStop).
   items: PropTypes.array,
   // Ação "Restaurar" por item (handoff 0059) — só com routing:schedule_item:cancel.
   canRestore: PropTypes.bool,
   // Chamado após restore com sucesso (ou corrida 422) — pai recarrega o snapshot.
   onRestored: PropTypes.func,
   onClose: PropTypes.func.isRequired,
};
